import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * Custom hook to manage user profile editing
 */
export function useProfile(user, refreshUser) {
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileCity, setProfileCity] = useState('');
  const [profileCountry, setProfileCountry] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Initialize profile form with user data when user changes
  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfileEmail(user.email || '');
      setProfileCity(user.city || '');
      setProfileCountry(user.country || '');
    }
  }, [user]);

  // Start editing
  const startEditing = useCallback(() => {
    setEditingProfile(true);
  }, []);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    // Reset to user data
    if (user) {
      setProfileName(user.name || '');
      setProfileCity(user.city || '');
      setProfileCountry(user.country || '');
    }
    setEditingProfile(false);
    setNewPassword('');
    setConfirmPassword('');
  }, [user]);

  // Save profile changes
  const saveProfile = useCallback(async () => {
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSavingProfile(true);
    try {
      const updates = {};
      
      if (profileName !== user?.name) updates.name = profileName;
      if (profileCity !== user?.city) updates.city = profileCity;
      if (profileCountry !== user?.country) updates.country = profileCountry;
      
      if (Object.keys(updates).length > 0) {
        await api.put('/auth/profile', updates);
      }
      
      if (newPassword) {
        await api.put('/auth/password', { password: newPassword });
        toast.success('Password updated successfully');
      }
      
      if (refreshUser) await refreshUser();
      
      toast.success('Profile updated successfully');
      setEditingProfile(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  }, [user, profileName, profileCity, profileCountry, newPassword, confirmPassword, refreshUser]);

  return {
    editingProfile,
    profileName,
    setProfileName,
    profileEmail,
    profileCity,
    setProfileCity,
    profileCountry,
    setProfileCountry,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    savingProfile,
    startEditing,
    cancelEditing,
    saveProfile,
  };
}

export default useProfile;
